/**
 * Order Helper Utilities
 * Helper functions for order-related operations.
 * 
 * Extracted from App.js for better code organization.
 */

/**
 * Order status constants - matches backend
 */
export const ORDER_STATUS = {
  CREATED: 'created',
  OPEN: 'open',
  IN_PROGRESS: 'in_progress',
  MAIL_AWAY: 'mail_away',
  MAIL_AWAY_RECEIVED: 'mail_away_received',
  COMPLETE: 'complete',
  CLARIFICATION: 'clarification',
  CLARIFICATION_RECEIVED: 'clarification_received',
  CANCELLED: 'cancelled'
};

/**
 * Status tabs configuration - same for all personas
 */
export const STATUS_TABS = [
  { value: 'all', label: 'All', color: 'bg-gray-100 text-gray-700 hover:bg-gray-200' },
  { value: 'open', label: 'Open', color: 'bg-blue-100 text-blue-700 hover:bg-blue-200' },
  { value: ORDER_STATUS.IN_PROGRESS, label: 'Pending', color: 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200' },
  { value: ORDER_STATUS.MAIL_AWAY, label: 'Mail-Away', color: 'bg-purple-100 text-purple-700 hover:bg-purple-200' },
  { value: ORDER_STATUS.MAIL_AWAY_RECEIVED, label: 'Mail-Away Received', color: 'bg-pink-100 text-pink-700 hover:bg-pink-200' },
  { value: ORDER_STATUS.COMPLETE, label: 'Complete', color: 'bg-green-100 text-green-700 hover:bg-green-200' },
  { value: ORDER_STATUS.CLARIFICATION, label: 'Clarification', color: 'bg-orange-100 text-orange-700 hover:bg-orange-200' },
  { value: ORDER_STATUS.CLARIFICATION_RECEIVED, label: 'Clarification Received', color: 'bg-amber-100 text-amber-700 hover:bg-amber-200' },
  { value: ORDER_STATUS.CANCELLED, label: 'Cancelled', color: 'bg-red-100 text-red-700 hover:bg-red-200' }
];

/**
 * Check if user is any type of processor
 * @param {string} role - User role
 * @returns {boolean}
 */
export const isProcessorRole = (role) => {
  const normalized = (role || '').toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_');
  return ['processor', 'tax_processor', 'title_search_processor', 'search_processor'].includes(normalized);
};

/**
 * Normalize status display for Order Desk persona ONLY
 * Maps legacy "created" status → "Open" for Order Desk UI consistency
 * @param {string} status - Order status
 * @param {string} userRole - User's role
 * @returns {string} Normalized status
 */
export const normalizeStatusForOrderDesk = (status, userRole) => {
  if (userRole === 'order_desk' && status?.toLowerCase() === 'created') {
    return 'open';
  }
  return status;
};

/**
 * Normalize assignee helper - handles null/undefined and different field names
 * @param {Object} assignee - Assignee object
 * @returns {Object} Normalized assignee with id and label
 */
export const normalizeAssignee = (assignee) => {
  if (!assignee || !assignee.id) {
    return { id: null, label: 'Unassigned' };
  }
  
  const label = 
    assignee.full_name?.trim() ||
    [assignee.first_name, assignee.last_name].filter(Boolean).join(' ').trim() ||
    'Unassigned';
  
  return { id: assignee.id, label };
};

/**
 * Normalize processor for dropdown options
 * @param {Object} proc - Processor object
 * @returns {Object} Normalized processor with value and label
 */
export const normalizeProcessor = (proc) => ({
  value: proc.id,
  label: proc.full_name?.trim() || 
         [proc.first_name, proc.last_name].filter(Boolean).join(' ').trim() || 
         proc.email ||
         'Unnamed'
});

/**
 * Get status badge variant based on status
 * @param {string} status - Order status
 * @returns {string} Badge variant
 */
export const getStatusBadgeVariant = (status) => {
  const variants = {
    'open': 'default',
    'created': 'secondary',
    'in_progress': 'default',
    'pending': 'default',
    'mail_away': 'secondary',
    'mail_away_received': 'secondary',
    'clarification': 'warning',
    'clarification_received': 'warning',
    'complete': 'success',
    'cancelled': 'destructive'
  };
  return variants[status?.toLowerCase()] || 'default';
};

/**
 * Check if an order is a tax product
 *
 * Feb 26, 2026 — Now workflow_type-aware (Option A architecture).
 *
 * Decision order:
 *   1. PREFERRED: `order.workflow_type` from the client_products catalog
 *      (backend enriches every order via `enrich_orders_with_workflow_type`).
 *      Returns true for TAX_SEARCH or MLS workflow types — same grouping the
 *      legacy whitelist used (Tax + MLS variants).
 *   2. FALLBACK: hardcoded canonical-name whitelist for backward compat with
 *      legacy orders that pre-date workflow_type enrichment, or orders whose
 *      backend response somehow lacks the field (defensive).
 *
 * This means admin-added custom Tax products (e.g. "Tax-MCTDT" backed by a
 * catalog row with workflow_type=TAX_SEARCH) are now recognized automatically
 * — no code change needed when new contract mappings are introduced.
 *
 * @param {Object} order - Order object
 * @returns {boolean}
 */
export const isTaxProduct = (order) => {
  if (!order) return false;

  // PREFERRED PATH — workflow_type from the catalog (admin-configurable).
  // TAX_SEARCH covers Tax Search + Update Tax Search + any future tax custom
  // products. MLS covers MLS + MLS Update + Update MLS — historically grouped
  // with tax in the UI (Tax Callback Sheet, Tax Cert workflow).
  const wt = order?.workflow_type;
  if (wt === 'TAX_SEARCH' || wt === 'MLS') return true;
  if (wt === 'TITLE_SEARCH' || wt === 'UTILITY') return false;

  // FALLBACK — legacy 5-string whitelist. Only consulted when workflow_type
  // is missing on the order payload (e.g. older endpoints not yet enriched,
  // or orders created before this field existed). Identical to pre-Feb-26
  // behaviour so no production regression for existing orders.
  const clientProduct = order?.client_product?.trim?.();
  return clientProduct === 'Tax Search' || clientProduct === 'Update Tax Search' ||
         clientProduct === 'MLS' || clientProduct === 'MLS Update' ||
         clientProduct === 'Update MLS';
};

/**
 * MLS product check (UI/display only). MLS / MLS Update / legacy Update MLS
 * are tax-workflow products internally, but MLS is its own operational view —
 * used to render product-specific labels like "MLS Callback Sheet".
 */
export const isMlsProduct = (order) => {
  if (!order) return false;
  if (order?.workflow_type === 'MLS') return true;
  const cp = order?.client_product?.trim?.();
  return cp === 'MLS' || cp === 'MLS Update' || cp === 'Update MLS';
};

/**
 * Product-aware callback-sheet label (display only — same route/behavior).
 */
export const getCallbackSheetLabel = (order) =>
  (isMlsProduct(order) ? 'MLS Callback Sheet' : 'Tax Callback Sheet');

/**
 * Title-Search product catalog mirror.
 *
 * These are the product names the backend tags with
 * `workflow_type == "TITLE_SEARCH"` in the `client_products` collection
 * (see the DB-driven check at `server.py::is_search_product_async`).
 * Kept in sync here so UI-only decisions (like "which file categories
 * to show in the Upload modal") don't need an extra backend round-trip.
 *
 * If a new Title Search product is added to the catalog, append its
 * `name` here too. The list is intentionally narrow — we match by the
 * exact canonical product name, never by client code.
 */
export const TITLE_SEARCH_PRODUCT_NAMES = new Set([
  'Recon',
  'Current Owner Search',
  'Two Owner Search',
  'Full Search',
  'AVR Search',
  // Feb 2026 — AVR display-name variants. Contract-mapping (Feb 2026)
  // collapses AVR products to either the canonical short name "AVR"
  // or a per-contract prefix like "MCRRC-AVR" / "NSMRC-AVR". The catalog
  // still stores them all under `workflow_type=TITLE_SEARCH`, so the
  // OBJECT-input path of `isSearchProduct` already resolves them
  // correctly via the workflow_type short-circuit. This whitelist is
  // only consulted as a *fallback* when the order payload (or a string
  // input) lacks workflow_type. We add the canonical short-name "AVR"
  // here so that fallback path still works on legacy/manual orders.
  // Per-contract variants ("MCRRC-AVR", "NSMRC-AVR", future "*-AVR")
  // are matched by suffix below in `isSearchProduct`.
  'AVR',
  'Deed Search',
  'Mortgage Search',
  'Legal and Vesting',
  'O&E',
  'Commercial Full Search',
  'Commercial COS',
]);

/**
 * Check if an order is a (Title) Search product.
 *
 * Feb 26, 2026 — Now workflow_type-aware (Option A architecture).
 *
 * Decision order:
 *   1. PREFERRED: when passed an order object with `workflow_type === 'TITLE_SEARCH'`
 *      from the backend catalog enrichment.
 *   2. FALLBACK: hardcoded `TITLE_SEARCH_PRODUCT_NAMES` whitelist. Required for
 *      (a) string inputs (callers that pass just a `client_product` name with
 *      no order context, e.g. `FileUploadModal.jsx`) and (b) legacy order
 *      payloads missing `workflow_type`.
 *
 * Used by the Upload Files modal to hide categories that aren't relevant to
 * Search workflows (Final Document, Email/Fax/Mail Evidence, Audio Recording)
 * and by Order Summary / Summary Tab for `showLoanId`. Adding a new Title
 * Search product to the catalog requires no code change on the order-object
 * path; only the string-input path still depends on the name whitelist.
 *
 * @param {Object|string} orderOrProduct - order object OR client_product string
 * @returns {boolean}
 */
export const isSearchProduct = (orderOrProduct) => {
  if (!orderOrProduct) return false;

  // Object input — check workflow_type first.
  if (typeof orderOrProduct === 'object') {
    const wt = orderOrProduct?.workflow_type;
    if (wt === 'TITLE_SEARCH') return true;
    if (wt === 'TAX_SEARCH' || wt === 'MLS' || wt === 'UTILITY') return false;
    // Fall through to name whitelist when workflow_type is absent.
  }

  const clientProduct =
    typeof orderOrProduct === 'string'
      ? orderOrProduct.trim()
      : orderOrProduct?.client_product?.trim?.();
  if (!clientProduct) return false;
  if (TITLE_SEARCH_PRODUCT_NAMES.has(clientProduct)) return true;
  // Feb 2026 — Per-contract AVR display variants ("MCRRC-AVR", "NSMRC-AVR",
  // and any future "<CONTRACT>-AVR" prefix). The catalog only stores the
  // canonical "AVR Search" with workflow_type=TITLE_SEARCH, so when the
  // dashboard renames display names through contract mapping these never
  // hit the exact-name whitelist. Match by the canonical "-AVR" / "AVR-"
  // boundary so it stays narrow (won't accidentally match "AVRC" or
  // "AAVRX"). The OBJECT path above already handles this via workflow_type;
  // this branch only runs for string inputs or legacy orders missing
  // workflow_type on the payload.
  const upper = clientProduct.toUpperCase();
  if (upper === 'AVR' || upper.endsWith('-AVR') || upper.startsWith('AVR-')) return true;
  return false;
};
